// Left navigation — role-scoped. Collapses between 220px (labels) and 64px (icons only);
// state persists. Nav items differ per role (internal staff / rep / client).
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Wordmark from '../Wordmark.jsx';
import { safeGet, safeSet } from '../../lib/storage.js';
import { useAuthStore } from '../../stores/authStore.js';
import { can, CAP } from '../../lib/access.js';
import {
  DashboardIcon,
  OrdersIcon,
  ClientsIcon,
  LookbookIcon,
  MessagesIcon,
  PaymentsIcon,
  DiscountIcon,
  AnalyticsIcon,
  TeamIcon,
  AccountIcon,
  NewOrderIcon,
  ChevronLeftIcon,
  CommissionIcon,
  CloseIcon,
} from '../icons.jsx';

const NAV = {
  INTERNAL_STAFF: {
    main: [
      { to: '/', label: 'Dashboard', Icon: DashboardIcon, end: true },
      { to: '/catalog', label: 'New order', Icon: NewOrderIcon, cap: CAP.CREATE_ORDER },
      { to: '/orders', label: 'Orders', Icon: OrdersIcon },
      { to: '/clients', label: 'Clients', Icon: ClientsIcon },
      // Marketing (emails + the lookbook PDF tool) for admins/managers; everyone else keeps Lookbooks.
      { to: '/marketing', label: 'Marketing', Icon: LookbookIcon, cap: CAP.MANAGE_MARKETING },
      { to: '/lookbooks', label: 'Lookbooks', Icon: LookbookIcon, notCap: CAP.MANAGE_MARKETING },
      { to: '/payments', label: 'Payments', Icon: PaymentsIcon },
      { to: '/receivables', label: 'Receivables', Icon: AnalyticsIcon, cap: CAP.MANAGE_PAYMENTS },
      { to: '/discounts', label: 'Promotions', Icon: DiscountIcon },
      { to: '/commissions', label: 'Commissions', Icon: CommissionIcon, cap: CAP.MANAGE_PAYMENTS },
      { to: '/messages', label: 'Messages', Icon: MessagesIcon },
      { to: '/settings', label: 'Settings', Icon: TeamIcon, cap: CAP.MANAGE_TEAM },
    ],
    footer: [{ to: '/account', label: 'Account', Icon: AccountIcon }],
  },
  REP: {
    // No dashboard — home is the combined order-history + commissions module.
    main: [
      { to: '/', label: 'Home', Icon: DashboardIcon, end: true },
      { to: '/clients', label: 'My clients', Icon: ClientsIcon },
      { to: '/catalog', label: 'New order', Icon: NewOrderIcon },
      { to: '/orders', label: 'Orders', Icon: OrdersIcon },
      { to: '/payments', label: 'Payments', Icon: PaymentsIcon },
      { to: '/commissions', label: 'Commissions', Icon: CommissionIcon },
      { to: '/messages', label: 'Messages', Icon: MessagesIcon },
    ],
    footer: [{ to: '/account', label: 'Account', Icon: AccountIcon }],
  },
  CLIENT: {
    // No dashboard — home is the Orders & Invoices module (order history + invoice downloads).
    main: [
      { to: '/', label: 'My orders', Icon: OrdersIcon, end: true },
      // Ordering happens here — "Catalog" made people think it was browse-only.
      { to: '/catalog', label: 'New order', Icon: NewOrderIcon },
      { to: '/invoices', label: 'Invoices & statements', Icon: PaymentsIcon },
      { to: '/promotions', label: 'Promotions', Icon: DiscountIcon },
      { to: '/messages', label: 'Messages', Icon: MessagesIcon },
    ],
    footer: [{ to: '/account', label: 'Account', Icon: AccountIcon }],
  },
};

const COLLAPSE_KEY = 'wholesale.sidebarCollapsed';

function NavItem({ to, label, Icon, end, collapsed, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      aria-label={label}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
          isActive
            ? 'bg-accent-light text-text-primary'
            : 'text-text-secondary hover:bg-surface-raised hover:text-text-primary'
        } ${collapsed ? 'justify-center px-0' : ''}`
      }
    >
      <Icon size={20} />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}

// Desktop (md+): a fixed, collapsible sidebar. Phones / small tablets: hidden, and opened from the top
// bar as a slide-out drawer (mobileOpen) — the 220px sidebar used to eat most of a phone screen.
export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role || 'CLIENT';
  const base = NAV[role] ?? NAV.CLIENT;
  // Hide nav entries the member's capability tier can't use (server still enforces).
  const visible = (items) => items.filter((i) => (!i.cap || can(user, i.cap)) && (!i.notCap || !can(user, i.notCap)));
  const nav = { main: visible(base.main), footer: visible(base.footer) };
  const [collapsed, setCollapsed] = useState(() => safeGet(COLLAPSE_KEY) === '1');

  // Close the drawer on navigation and on Escape.
  const location = useLocation();
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen, onClose]);

  const toggle = () =>
    setCollapsed((c) => {
      const next = !c;
      safeSet(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });

  return (
    <>
    {mobileOpen && (
      <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
        <button type="button" aria-label="Close menu" onClick={onClose} className="absolute inset-0 bg-black/40" />
        <aside className="absolute inset-y-0 left-0 flex w-[260px] max-w-[85vw] flex-col border-r border-line bg-surface shadow-xl">
          <div className="flex h-[57px] items-center justify-between border-b border-line px-5">
            <Wordmark imgClass="h-5" showLabel={false} />
            <button type="button" onClick={onClose} aria-label="Close menu" className="rounded-md p-1.5 text-text-secondary hover:bg-surface-raised">
              <CloseIcon size={20} />
            </button>
          </div>
          <nav aria-label="Primary" className="flex-1 space-y-1 overflow-y-auto p-2">
            {nav.main.map((item) => (
              <NavItem key={item.to} {...item} onNavigate={onClose} />
            ))}
          </nav>
          <div className="space-y-1 border-t border-line p-2">
            {nav.footer.map((item) => (
              <NavItem key={item.to} {...item} onNavigate={onClose} />
            ))}
          </div>
        </aside>
      </div>
    )}
    <aside
      className={`hidden shrink-0 flex-col border-r border-line bg-surface transition-all duration-200 md:flex ${
        collapsed ? 'w-16' : 'w-[220px]'
      }`}
    >
      <div
        className={`flex h-[57px] items-center border-b border-line ${collapsed ? 'justify-center' : 'px-5'}`}
      >
        <Wordmark compact={collapsed} imgClass="h-5" showLabel={false} />
      </div>

      <nav aria-label="Primary" className="flex-1 space-y-1 p-2">
        {nav.main.map((item) => (
          <NavItem key={item.to} {...item} collapsed={collapsed} />
        ))}
      </nav>

      <div className="space-y-1 border-t border-line p-2">
        {nav.footer.map((item) => (
          <NavItem key={item.to} {...item} collapsed={collapsed} />
        ))}
        <button
          type="button"
          onClick={toggle}
          title={collapsed ? 'Expand' : 'Collapse'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-text-muted transition hover:bg-surface-raised hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
            collapsed ? 'justify-center px-0' : ''
          }`}
        >
          <span className={`transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}>
            <ChevronLeftIcon size={18} />
          </span>
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
    </>
  );
}
